import React, { useEffect, useState } from 'react'

import Immutable from 'immutable'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'

import { useIsSkipTheWizardEnabled } from 'hooks/useIsSkipTheWizardEnabled'
import {
  menuClosePropositionTileAction,
  trackCloseThePropositionTile,
  trackShowThePropositionTile,
} from 'routes/Menu/actions/menuRecipeDetails'
import { CaloriesTutorial } from 'routes/Menu/components/MenuPreferences'
import { RecipeTileBridge } from 'routes/Menu/components/RecipeTile/RecipeTileBridge'
import { useBasket, useStock } from 'routes/Menu/domains/basket'
import { CollectionId } from 'routes/Menu/domains/collections/constants'
import { getHasClosedPropositionTile } from 'routes/Menu/selectors/menu'

import { useAuth } from '../../domains/auth/useAuth'
import { CTAToAllRecipes } from '../CTAToAllRecipes'
import { PropositionTile } from './PropositionTile'
import { buildTracker } from './recipeListTracking'
import { useTracking } from './useTracking'

import css from './RecipeList.css'

export const RecipeList = ({ recipes, currentCollectionId, menuPreferences }) => {
  const dispatch = useDispatch()
  const track = useTracking()

  const { isAuthenticated } = useAuth()
  const isSkipTheWizardEnabled = useIsSkipTheWizardEnabled()

  const hasClosedTile = useSelector(getHasClosedPropositionTile)

  const [isOnAllRecipesCategory, setIsOnAllRecipesCategory] = useState(
    currentCollectionId === CollectionId.AllRecipes,
  )
  const [showPropositionTile, setShowPropositionTile] = useState(
    isSkipTheWizardEnabled && !isAuthenticated && !hasClosedTile && isOnAllRecipesCategory,
  )

  useEffect(() => {
    const isOnAllRecipes = currentCollectionId === CollectionId.AllRecipes
    setIsOnAllRecipesCategory(isOnAllRecipes)
    setShowPropositionTile(
      isSkipTheWizardEnabled && !isAuthenticated && !hasClosedTile && isOnAllRecipes,
    )
  }, [currentCollectionId, isAuthenticated, isSkipTheWizardEnabled, hasClosedTile])

  useEffect(() => buildTracker({ recipes, currentCollectionId, track })(), [])

  useEffect(() => {
    if (showPropositionTile) {
      dispatch(trackShowThePropositionTile())
    }
    // there is no scenario in which we want to fire two tracking events
    // for showing the sell the proposition tile
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const closeTile = () => {
    setShowPropositionTile(false)
    dispatch(menuClosePropositionTileAction())
    dispatch(trackCloseThePropositionTile())
  }

  const { numPortions, canAddRecipes, addRecipe, removeRecipe, reachedLimit, isRecipeInBasket } =
    useBasket()

  const { isRecipeOutOfStock } = useStock()

  const renderPropositionTile = () => <PropositionTile onClose={closeTile} />

  return (
    <div className={css.threeTileRecipeList}>
      {showPropositionTile && renderPropositionTile()}
      {recipes.map((value, index) => (
        <React.Fragment key={value.reference}>
          {index === 5 && <CaloriesTutorial />}
          <RecipeTileBridge
            recipeReference={value.reference}
            recipe={value.recipe}
            originalId={value.originalId}
            collectionId={currentCollectionId}
            menuPreferences={menuPreferences}
            numPortions={numPortions}
            canAddRecipes={canAddRecipes}
            addRecipe={addRecipe}
            removeRecipe={removeRecipe}
            reachedLimit={reachedLimit}
            isRecipeInBasket={isRecipeInBasket}
            isRecipeOutOfStock={isRecipeOutOfStock}
          />
        </React.Fragment>
      ))}
      <CTAToAllRecipes />
    </div>
  )
}

RecipeList.propTypes = {
  recipes: PropTypes.instanceOf(Immutable.List).isRequired,
  currentCollectionId: PropTypes.string.isRequired,
  menuPreferences: PropTypes.shape({
    display_preferences: PropTypes.bool,
  }),
}

RecipeList.defaultProps = {
  menuPreferences: null,
}
